<template>
  <div class="pdfjs-wrap">
    <div class="pdfjs-toolbar">
      <button class="btn" @click="prevPage" :disabled="pageNumber <= 1">‹</button>
      <span class="info">{{ pageNumber }} / {{ numPages }}</span>
      <button class="btn" @click="nextPage" :disabled="pageNumber >= numPages">›</button>

      <span class="spacer"></span>

      <button class="btn" @click="zoomOut" :disabled="scale <= 0.6">-</button>
      <span class="info">{{ Math.round(scale * 100) }}%</span>
      <button class="btn" @click="zoomIn" :disabled="scale >= 2.0">+</button>

      <span class="spacer"></span>

      <button class="btn" @click="reload">Neu laden</button>
      <a class="btn" :href="props.src" target="_blank" rel="noopener">PDF öffnen</a>
    </div>

    <div ref="containerRef" class="pdfjs-container">
      <div v-if="loading" class="hint">PDF wird geladen…</div>
      <div v-if="error" class="hint error">{{ error }}</div>

      <canvas ref="canvasRef" class="pdfjs-page"></canvas>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */

import { onMounted, onBeforeUnmount, ref, watch, shallowRef, markRaw } from "vue";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// Worker liegt in /public/pdf.worker.min.js
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const props = defineProps({
  src: { type: String, required: true },
});

const containerRef = ref(null);
const canvasRef = ref(null);

const pdfDoc = shallowRef(null); // wichtig: nicht reactive/proxy
const loading = ref(false);
const error = ref("");

const scale = ref(1.0);

const pageNumber = ref(1);
const numPages = ref(1);

let resizeObserver = null;

function getFitScale(page) {
  const container = containerRef.value;
  if (!container) return scale.value;

  const viewport1 = page.getViewport({ scale: 1 });
  const maxWidth = Math.max(100, container.clientWidth - 10);
  const fit = maxWidth / viewport1.width;

  return fit * scale.value;
}

async function loadPdf() {
  loading.value = true;
  error.value = "";
  pdfDoc.value = null;

  try {
    if (typeof props.src !== "string" || !props.src.trim()) {
      throw new Error("PDF src ist kein String oder leer: " + String(props.src));
    }

    const task = pdfjsLib.getDocument({
      url: props.src,
      withCredentials: false,
    });

    const doc = await task.promise;
    pdfDoc.value = markRaw(doc);

    numPages.value = doc.numPages;
    pageNumber.value = Math.min(pageNumber.value, doc.numPages);

    await renderCurrentPage();
  } catch (e) {
    error.value = "PDF konnte nicht geladen werden: " + (e?.message || e);
  } finally {
    loading.value = false;
  }
}

async function renderCurrentPage() {
  const doc = pdfDoc.value;
  const canvas = canvasRef.value;
  if (!doc || !canvas) return;

  const page = await doc.getPage(pageNumber.value);

  const s = getFitScale(page);
  const viewport = page.getViewport({ scale: s });

  const ctx = canvas.getContext("2d", { alpha: false });

  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(viewport.width * dpr);
  canvas.height = Math.floor(viewport.height * dpr);
  canvas.style.width = Math.floor(viewport.width) + "px";
  canvas.style.height = Math.floor(viewport.height) + "px";

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  await page.render({ canvasContext: ctx, viewport }).promise;
}

function nextPage() {
  if (pageNumber.value < numPages.value) pageNumber.value++;
}

function prevPage() {
  if (pageNumber.value > 1) pageNumber.value--;
}

function zoomIn() {
  scale.value = Math.min(2.0, +(scale.value + 0.1).toFixed(2));
}

function zoomOut() {
  scale.value = Math.max(0.6, +(scale.value - 0.1).toFixed(2));
}

function reload() {
  loadPdf();
}

// wenn Seite geändert wird -> neu rendern
watch(pageNumber, async () => {
  await renderCurrentPage();
});

// wenn Zoom geändert wird -> neu rendern
watch(scale, async () => {
  await renderCurrentPage();
});

// wenn src sich ändert -> neu laden
watch(
  () => props.src,
  async () => {
    pageNumber.value = 1;
    await loadPdf();
  }
);

onMounted(async () => {
  await loadPdf();

  // bei Resize (Handy drehen etc.) neu rendern
  resizeObserver = new ResizeObserver(async () => {
    await renderCurrentPage();
  });

  if (containerRef.value) resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
  if (resizeObserver && containerRef.value) resizeObserver.unobserve(containerRef.value);
  resizeObserver = null;
});
</script>

<style scoped>
.pdfjs-wrap {
  width: 100%;
}

.pdfjs-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  flex-wrap: wrap;
}

.btn {
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.info {
  min-width: 70px;
  text-align: center;
}

.spacer {
  flex: 1;
}

.pdfjs-container {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px;
  overflow: auto;
}

.pdfjs-page {
  display: block;
  margin: 0 auto;
  border-radius: 6px;
}

.hint {
  padding: 12px;
  text-align: center;
}

.error {
  color: #b00020;
}
</style>
